import React, { useState, useRef, useEffect } from 'react'
import { SelectFieldProps } from './types'
import { SelectOption } from '../../types/FormTypes'

function SelectField({
  value,
  onChange,
  options,
  multiple = false,
  searchable = false,
  creatable = false,
  loading = false,
  loadOptions,
  placeholder,
  disabled = false,
  required = false,
  className = '',
  name,
  id,
  ...props
}: SelectFieldProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [remoteOptions, setRemoteOptions] = useState<SelectOption[]>([])
  const selectRef = useRef<HTMLDivElement>(null)
  
  const allOptions = loadOptions ? remoteOptions : options
  
  const filteredOptions = searchTerm
    ? allOptions.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : allOptions

  const selectedValues = Array.isArray(value) ? value : [value]
  const selectedOptions = allOptions.filter(option =>
    selectedValues.includes(option.value)
  )

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (loadOptions && searchTerm) {
      const loadRemoteOptions = async () => {
        try {
          const results = await loadOptions(searchTerm)
          setRemoteOptions(results)
        } catch (error) {
          console.error('Error loading options:', error)
        }
      }
      loadRemoteOptions()
    }
  }, [searchTerm, loadOptions])

  const handleOptionSelect = (option: SelectOption) => {
    if (multiple) {
      const newValues = selectedValues.includes(option.value)
        ? selectedValues.filter(v => v !== option.value)
        : [...selectedValues, option.value]
      
      // Ensure type consistency for the array
      const firstValueType = newValues.length > 0 ? typeof newValues[0] : 'string'
      const isAllStrings = newValues.every(v => typeof v === 'string')
      const isAllNumbers = newValues.every(v => typeof v === 'number')
      
      if (isAllStrings) {
        onChange(newValues as string[])
      } else if (isAllNumbers) {
        onChange(newValues as number[])
      } else {
        // Mixed types - convert all to strings for consistency
        onChange(newValues.map(v => String(v)))
      }
    } else {
      onChange(option.value)
      setIsOpen(false)
      setSearchTerm('')
    }
  }

  const handleCreateOption = () => {
    if (creatable && searchTerm) {
      const newOption: SelectOption = {
        value: searchTerm,
        label: searchTerm
      }
      handleOptionSelect(newOption)
      setSearchTerm('')
    }
  }

  const baseClasses = 'tw-relative tw-w-full'
  const triggerClasses = 'tw-w-full tw-rounded-lg tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-bg-white dark:tw-bg-gray-800 tw-px-3 tw-py-2 tw-text-sm tw-text-left focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 disabled:tw-bg-gray-50 disabled:tw-text-gray-500 disabled:tw-cursor-not-allowed'

  return (
    <div className={`${baseClasses} ${className}`} ref={selectRef}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={triggerClasses}
        {...props}
      >
        <div className="tw-flex tw-items-center tw-justify-between">
          <span className={selectedOptions.length > 0 ? 'tw-text-gray-900 dark:tw-text-white' : 'tw-text-gray-500'}>
            {selectedOptions.length > 0 ? (
              multiple ? (
                selectedOptions.length === 1 ? selectedOptions[0].label : `${selectedOptions.length} selected`
              ) : (
                selectedOptions[0].label
              )
            ) : (
              placeholder || 'Select...'
            )}
          </span>
          <span className="material-symbols-outlined tw-text-gray-400 tw-ml-2">
            {isOpen ? 'expand_less' : 'expand_more'}
          </span>
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="tw-absolute tw-z-50 tw-mt-1 tw-w-full tw-bg-white dark:tw-bg-gray-800 tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-rounded-lg tw-shadow-lg tw-max-h-60 tw-overflow-auto">
          {/* Search input */}
          {searchable && (
            <div className="tw-p-2 tw-border-b tw-border-gray-200 dark:tw-border-gray-700">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search options..."
                className="tw-w-full tw-px-3 tw-py-1 tw-text-sm tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-rounded tw-bg-white dark:tw-bg-gray-800 tw-text-gray-900 dark:tw-text-white placeholder:tw-text-gray-500 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
              />
            </div>
          )}

          {/* Loading state */}
          {loading && (
            <div className="tw-p-3 tw-text-center tw-text-gray-500">
              <span className="tw-animate-spin tw-inline-block tw-w-4 tw-h-4 tw-border-2 tw-border-gray-300 tw-border-t-blue-600 tw-rounded-full"></span>
              <span className="tw-ml-2">Loading...</span>
            </div>
          )}

          {/* Options */}
          {!loading && filteredOptions.length > 0 && (
            <div className="tw-py-1">
              {filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(option.value)
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => !option.disabled && handleOptionSelect(option)}
                    disabled={option.disabled}
                    className={`tw-w-full tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-flex tw-items-center tw-justify-between hover:tw-bg-gray-100 dark:hover:tw-bg-gray-700 disabled:tw-cursor-not-allowed disabled:tw-opacity-50 ${
                      isSelected ? 'tw-bg-blue-50 dark:tw-bg-blue-900/20 tw-text-blue-600 dark:tw-text-blue-400' : 'tw-text-gray-900 dark:tw-text-white'
                    }`}
                  >
                    <span>{option.label}</span>
                    {isSelected && (
                      <span className="material-symbols-outlined tw-text-blue-600 dark:tw-text-blue-400 tw-text-base">
                        check
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          )}

          {/* Create option */}
          {creatable && searchTerm && !filteredOptions.some(opt => opt.label.toLowerCase() === searchTerm.toLowerCase()) && (
            <div className="tw-py-1 tw-border-t tw-border-gray-200 dark:tw-border-gray-700">
              <button
                type="button"
                onClick={handleCreateOption}
                className="tw-w-full tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-text-blue-600 dark:tw-text-blue-400 hover:tw-bg-gray-100 dark:hover:tw-bg-gray-700"
              >
                Create "{searchTerm}"
              </button>
            </div>
          )}

          {/* No options */}
          {!loading && filteredOptions.length === 0 && !creatable && (
            <div className="tw-p-3 tw-text-center tw-text-gray-500">
              No options found
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SelectField